import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import {
  colorNameToRgba,
  statusToIDSColor,
  IdsColorValue,
  convertColorValue
} from '../../utils/ids-color-utils/ids-color-utils';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-slider.scss';
import IdsDraggable from '../ids-draggable/ids-draggable';
import IdsText from '../ids-text/ids-text';

type IdsSliderType = 'single' | 'range' | 'step';

type IdsSliderTrackBounds = {
  BOTTOM: number;
  LEFT: number;
  RIGHT: number;
  TOP: number;
};

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_TYPE = 'single';
const DEFAULT_TRACKER_BOUNDS = {
  BOTTOM: NaN,
  LEFT: NaN,
  RIGHT: NaN,
  TOP: NaN
};

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Slider Component
 * @type {IdsSlider}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 */
@customElement('ids-slider')
@scss(styles)
export default class IdsSlider extends Base {
  DEFAULT_MIN = DEFAULT_MIN;

  DEFAULT_MAX = DEFAULT_MAX;

  DEFAULT_TYPE = DEFAULT_TYPE;

  #trackBounds: IdsSliderTrackBounds = DEFAULT_TRACKER_BOUNDS;

  #isSettingAttribute = false;

  #label = '';

  #labelSecondary = '';

  #labels: string[] = [];

  #isRTL = false;

  #mouseHover = false;

  #percent = NaN;

  #percentSecondary = NaN;

  slider?: HTMLElement | null;

  trackArea?: HTMLElement | null;

  progressTrack?: HTMLElement | null;

  track?: HTMLElement | null;

  tickContainer?: HTMLElement | null;

  thumb?: HTMLElement | null;

  thumbDraggable?: IdsDraggable | null;

  thumbShadow?: HTMLElement | null;

  tooltip?: HTMLElement | null;

  tooltipText?: IdsText | null;

  tooltipPin?: HTMLElement | null;

  firstTick?: HTMLElement | null;

  lastTick?: HTMLElement | null;

  thumbSecondary?: HTMLElement | null;

  thumbDraggableSecondary?: IdsDraggable | null;

  thumbShadowSecondary?: HTMLElement | null;

  thumbSecondaryDraggable: any;

  tooltipSecondary?: HTMLElement | null;

  tooltipTextSecondary?: IdsText | null;

  tooltipPinSecondary?: HTMLElement | null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.slider = this.container?.querySelector('.slider');
    this.trackArea = this.container?.querySelector('.track-area');
    this.progressTrack = this.container?.querySelector('.track-progress');
    this.track = this.container?.querySelector('.track');
    this.tickContainer = this.container?.querySelector('.tick-container');

    this.#mouseHover = false;
    this.thumb = this.container?.querySelector('.thumb');
    this.thumbDraggable = this.container?.querySelector('.thumb-draggable');
    this.thumbShadow = this.container?.querySelector('.thumb-shadow');
    this.tooltip = this.container?.querySelector('.tooltip');
    this.tooltipText = this.container?.querySelector('.tooltip .text');
    this.tooltipPin = this.container?.querySelector('.tooltip .pin');
    this.lastTick = this.container?.querySelector('.tick:last-child');
    this.firstTick = this.container?.querySelector('.tick:first-child');

    if (this.type === 'range') {
      this.thumbSecondary = this.container?.querySelector('.thumb.secondary');
      this.thumbDraggableSecondary = this.container?.querySelector('.thumb-draggable.secondary');
      this.thumbShadowSecondary = this.container?.querySelector('.thumb-shadow.secondary');
      this.tooltipSecondary = this.container?.querySelector('.tooltip.secondary');
      this.tooltipTextSecondary = this.container?.querySelector('.tooltip.secondary .text.secondary');
      this.tooltipPinSecondary = this.container?.querySelector('.tooltip .pin.secondary');
    } else {
      this.container?.querySelector('.thumb-draggable.secondary')?.remove();
    }

    this.#attachEventListeners();
    this.#attachUIStyles();
    this.#attachARIA();
    this.#setVertical();
    this.#setStepTicks();
    this.#setStepLabels();

    // @TODO find a better way to apply animation/transition rules after the component loads (#698)
    setTimeout(() => {
      this.#toggleAnimations(true);
      this.#isInitialRender = false;
    }, 300);
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [
      attributes.COLOR,
      attributes.DISABLED,
      attributes.LABEL,
      attributes.LABEL_SECONDARY,
      attributes.MIN,
      attributes.MAX,
      attributes.READONLY,
      attributes.STEP_INTERVAL,
      attributes.STEP_NUMBER,
      attributes.SHOW_TOOLTIP,
      attributes.TYPE,
      attributes.VALUE,
      attributes.VALUE_SECONDARY,
      attributes.VERTICAL,
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */

  template(): string {
    const disabledClass = this.disabled ? ' disabled' : '';
    const readonlyClass = this.readonly ? ' readonly' : '';
    const draggableTabIndex = (!disabledClass.length) ? '0' : '-1';
    const label = this.label ? `<ids-text font-size="12" type="h1">${this.label}</ids-text>` : '';
    const labelSecondary = this.labelSecondary ? `<ids-text font-size="10" type="h2">${this.labelSecondary}</ids-text>` : '';

    return `
      ${label}
      ${labelSecondary}
      <div class="ids-slider${disabledClass}${readonlyClass}">
        <div class="slider">
          <div class="track-area">
            <ids-draggable tabindex="${draggableTabIndex}" class="thumb-draggable" axis="${this.vertical ? 'y' : 'x'}" parent-containment>
              <div class="thumb-shadow"></div>
              <div class="thumb">
                <div class="tooltip">
                  <ids-text class="text">${this.value}</ids-text>
                  <div class="pin"></div>
                </div>
              </div>
            </ids-draggable>
            <ids-draggable tabindex="${draggableTabIndex}" class="thumb-draggable secondary" axis="${this.vertical ? 'y' : 'x'}" parent-containment>
              <div class="thumb-shadow secondary"></div>
              <div class="thumb secondary">
                <div class="tooltip secondary">
                  <ids-text class="text secondary">${this.valueSecondary}</ids-text>
                  <div class="pin secondary"></div>
                </div>
              </div>
            </ids-draggable>
          </div>
          <div class="track">
          <div class="track-progress"></div>
            <div class="tick-container">
              <span class="tick"></span>
              <span class="tick"></span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Controls Slider disabled state
   * @param {boolean | string} value If true, will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      if (this.readonly) this.readonly = false;
      this.setAttribute(attributes.DISABLED, '');
      this.container?.classList.add(attributes.DISABLED);
      this.thumbDraggable?.setAttribute(attributes.DISABLED, 'true');
      this.thumbDraggable?.setAttribute(attributes.TABINDEX, '-1');
      this.#updateTooltipDisplay(true);
      this.updateThresholdTickColors();
      if (this.type === 'range') {
        this.thumbDraggableSecondary?.setAttribute(attributes.DISABLED, 'true');
        this.thumbDraggableSecondary?.setAttribute(attributes.TABINDEX, '-1');
        this.#updateTooltipDisplay(true, 'secondary');
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      this.container?.classList.remove(attributes.DISABLED);
      this.thumbDraggable?.setAttribute(attributes.DISABLED, 'false');
      this.thumbDraggable?.setAttribute(attributes.TABINDEX, '0');
      if (this.type === 'range') {
        this.thumbDraggableSecondary?.setAttribute(attributes.DISABLED, 'false');
        this.thumbDraggableSecondary?.setAttribute(attributes.TABINDEX, '0');
      }
    }
    this.#updateColor();
  }

  /**
   * Get whether the slider is disabled
   * @returns {boolean | string} true if disabled
   */
  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   * Controls Slider readonly state
   * @param {boolean | string} value If true, will set `readonly` attribute
   */
  set readonly(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      if (this.disabled) this.disabled = false;
      this.setAttribute(attributes.READONLY, '');
      this.container?.classList.add(attributes.READONLY);
      this.thumbDraggable?.setAttribute(attributes.DISABLED, 'true');
      if (this.type === 'range') {
        this.thumbDraggableSecondary?.setAttribute(attributes.DISABLED, 'true');
        this.#updateTooltipDisplay(true, 'secondary');
      }
    } else {
      this.removeAttribute(attributes.READONLY);
      this.container?.classList.remove(attributes.READONLY);
      this.thumbDraggable?.setAttribute(attributes.DISABLED, 'false');
      if (this.type === 'range') this.thumbDraggableSecondary?.setAttribute(attributes.DISABLED, 'false');
    }
    this.#updateColor();
  }

  /**
   * Get whether the slider is readonly
   * @returns {boolean | string} true if readonly
   */
  get readonly(): boolean | string {
    return this.hasAttribute(attributes.READONLY);
  }

  /**
   * Modifies the primary Slider thumb's label contents
   * @param {string} value the label text contents
   */
  set label(value: string) {
    const safeValue = stripHTML(value);
    const currentValue = this.#label;
    if (safeValue !== currentValue) {
      if (safeValue.length) {
        this.#label = safeValue;
        this.setAttribute(attributes.LABEL, safeValue);
      } else {
        this.#label = '';
        this.removeAttribute(attributes.LABEL);
      }

      if (this.ariaLabel.length === 0) {
        this.ariaLabel = safeValue;
      }
    }
  }

  /**
   * @returns {string} the primary Slider thumb's label contents
   */
  get label(): string {
    return stripHTML(this.getAttribute(attributes.LABEL) ?? '');
  }

  /**
   * Set the aria label for slider
   * @param {string} value the aria label text contents
   */
  set ariaLabel(value: string) {
    const safeValue = stripHTML(value);
    const currentValue = this.getAttribute(htmlAttributes.ARIA_LABEL);
    if (safeValue !== currentValue) {
      if (safeValue.length) {
        this.setAttribute(htmlAttributes.ARIA_LABEL, safeValue);
        this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_LABEL, safeValue);
      } else {
        this.removeAttribute(htmlAttributes.ARIA_LABEL);
        this.thumbDraggable?.removeAttribute(htmlAttributes.ARIA_LABEL);
      }
    }
  }

  /**
   * Aria label for slider
   * @returns {string} the aria label for slider
   */
  get ariaLabel(): string {
    return stripHTML(this.getAttribute(htmlAttributes.ARIA_LABEL) ?? '');
  }

  /**
   * Set the aria label for the secondary draggable
   * @param {string} value the aria label text contents
   */
  set ariaLabelSecondary(value: string) {
    const safeValue = stripHTML(value);
    const currentValue = this.thumbDraggableSecondary?.getAttribute(htmlAttributes.ARIA_LABEL);
    if (safeValue !== currentValue) {
      if (safeValue.length) {
        this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_LABEL, safeValue);
      } else {
        this.thumbDraggableSecondary?.removeAttribute(htmlAttributes.ARIA_LABEL);
      }
    }
  }

  /**
   * Aria label for the secondary `
   * @returns {string} the aria label text contents
   */
  get ariaLabelSecondary(): string {
    return stripHTML(this.thumbDraggableSecondary?.getAttribute(htmlAttributes.ARIA_LABEL) ?? '');
  }

  /**
   * Modifies the primary Slider thumb's label contents
   * @param {string} value the label text contents
   */
  set labelSecondary(value: string) {
    const safeValue = stripHTML(value);
    const currentValue = this.#labelSecondary;
    if (safeValue !== currentValue) {
      if (safeValue.length) {
        this.#labelSecondary = safeValue;
        this.setAttribute(attributes.LABEL_SECONDARY, safeValue);
      } else {
        this.#labelSecondary = '';
        this.removeAttribute(attributes.LABEL_SECONDARY);
      }

      if (this.ariaLabelSecondary.length === 0) {
        this.ariaLabelSecondary = safeValue;
      }
    }
  }

  /**
   * @returns {string} the primary Slider thumb's label contents
   */
  get labelSecondary(): string {
    return stripHTML(this.getAttribute(attributes.LABEL_SECONDARY) ?? '');
  }

  /**
   * Set the orientation of the slider
   * @param {boolean} value Whether the orientation is vertical or horizontal
   */
  set vertical(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.VERTICAL, 'true');
    } else {
      this.removeAttribute(attributes.VERTICAL);
    }
    this.#setVertical();
    this.#setStepLabels();
    this.#setStepTicks();
    this.#updateColor();
  }

  /**
   * Get whether the slider is vertical
   * @returns {boolean} true if vertical
   */
  get vertical() {
    return this.hasAttribute(attributes.VERTICAL);
  }

  #setVertical() {
    if (this.vertical) {
      this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_ORIENTATION, 'vertical');
      this.thumbDraggable?.setAttribute('axis', 'y');
      this.thumbDraggableSecondary?.setAttribute('axis', 'y');
      if (this.type === 'range') this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_ORIENTATION, 'vertical');
      this.container?.classList.add('vertical');
      this.slider?.classList.add('vertical');
      this.progressTrack?.classList.add('vertical');
      this.track?.classList.add('vertical');
      this.trackArea?.classList.add('vertical');
      this.tickContainer?.classList.add('vertical');
      this.tooltip?.classList.add('vertical');
      if (this.type === 'range') this.tooltipSecondary?.classList.add('vertical');
      this.tooltipPin?.classList.add('vertical');
      if (this.type === 'range') this.tooltipPinSecondary?.classList.add('vertical');
    } else {
      this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_ORIENTATION, 'horizontal');
      this.thumbDraggable?.setAttribute('axis', 'x');
      this.thumbDraggableSecondary?.setAttribute('axis', 'x');
      if (this.type === 'range') this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_ORIENTATION, 'horizontal');
      this.container?.classList.remove('vertical');
      this.slider?.classList.remove('vertical');
      this.progressTrack?.classList.remove('vertical');
      this.track?.classList.remove('vertical');
      this.trackArea?.classList.remove('vertical');
      this.tickContainer?.classList.remove('vertical');
      this.tooltip?.classList.remove('vertical');
      if (this.type === 'range') this.tooltipSecondary?.classList.remove('vertical');
      this.tooltipPin?.classList.remove('vertical');
      if (this.type === 'range') this.tooltipPinSecondary?.classList.remove('vertical');
    }
  }

  /**
   * Keep track of RTL
   * @param {boolean} value Whether or not RTL is in effect
   */
  set isRTL(value: boolean) {
    if (value !== this.isRTL) {
      this.#isRTL = value;
      this.#moveThumb();
      this.#updateProgressBar();
      if (this.type === 'range') this.#moveThumb('secondary');
    }
  }

  /**
   * Get whether the slider is displayed in RTL mode
   * @returns {boolean} true if RTL
   */
  get isRTL(): boolean {
    if (typeof this.#isRTL === 'undefined') this.#isRTL = false;
    return this.#isRTL;
  }

  /**
   * Handle Languages Changes
   */
  onLanguageChange = () => {
    const isRTL = this.localeAPI.isRTL();
    this.isRTL = isRTL;
  };

  /**
   * Helper method to update the UI of the tooltip and its text
   * @private
   * @param {number} value the text the tooltip should display
   * @param {string} primaryOrSecondary which tooltip to update
   */
  #updateTooltip(value?: number, primaryOrSecondary?: string): void {
    let tooltipText = this.tooltipText;
    let type = 'primary';

    if (primaryOrSecondary === 'secondary') {
      tooltipText = this.tooltipTextSecondary;
      type = 'secondary';
    }

    let containsDecimals = false;

    for (let i = 0; i < this.ticks.length; i++) {
      if (this.ticks[i].element.innerHTML.indexOf('.') > -1 || this.ticks[i].element.innerHTML.indexOf(',') > -1) {
        containsDecimals = true;
        break;
      }
    }
    const tooltipValue = this.type === 'step' && containsDecimals ? String(Number(value).toFixed(2)) : String(Number(value).toFixed(0));
    if (tooltipText) tooltipText.innerHTML = tooltipValue;

    if (this.type !== 'step') {
      this.#updateTooltipDisplay(false, type);
    }
  }

  /**
   * Get the tick elements and their values
   * @returns {{ element: HTMLElement, value: number }[]} array of tick objects
   */
  get ticks() : { element: HTMLElement, value: number }[] {
    const ticks = Array.from(this.container?.querySelectorAll('.tick') || []);
    const map = ticks.map((tick, i) => ({
      element: tick as HTMLElement,
      // eslint-disable-next-line no-nested-ternary
      value: Number.isNaN(Number(tick.textContent))
        // eslint-disable-next-line max-len
        ? this.vertical ? (this.labels[this.labels.length - 1 - i] as any).value : (this.labels[i] as any)?.value : Number(tick.textContent)
    }));
    return map;
  }

  /**
   * Helper method to update the UI of the progress track bar
   * @private
   */
  #updateProgressBar(): void {
    if (this.type !== 'range') {
      this.slider?.style.setProperty('--percentStart', '0');
      this.slider?.style.setProperty('--percentEnd', String(this.percent));
    } else {
      const minPercent = Math.min(this.percent, this.percentSecondary);
      const maxPercent = Math.max(this.percent, this.percentSecondary);
      this.slider?.style.setProperty('--percentStart', String(minPercent));
      this.slider?.style.setProperty('--percentEnd', String(maxPercent));

      if (this.#trackBounds) {
        const {
          TOP,
          BOTTOM,
          LEFT,
          RIGHT
        } = this.#trackBounds;

        const startPos = this.vertical ? BOTTOM : LEFT;
        const endPos = this.vertical ? TOP : RIGHT;
        const centered = false;
        let trans = this.#calcTranslateFromPercent(startPos, endPos, minPercent, centered);
        if (this.vertical || this.isRTL) { trans *= -1; }
        const transString = this.vertical ? `translate(0, ${trans}px)` : `translate(${trans}px, 0)`;
        this.progressTrack?.style.setProperty('transform', transString);
      }
    }
  }

  /**
   * Set the labels to display on each step/tick mark (only applicable to step sliders)
   * @param {Array<string>} array the list of labels to set
   */
  set labels(array) {
    this.#labels = array;
    this.#setStepLabels();
  }

  /**
   * Get the labels for step/tick marks
   * @returns {Array<string>} array of labels
   */
  get labels(): Array<string> {
    return this.#labels || [];
  }

  /**
   * Helper method to update the labels on the UI according to stepNumber and labels
   * @private
   */
  #setStepLabels(): void {
    if (this.type !== 'step') return;

    const labels = this.labels;
    const labelsLength = labels?.length || 0;
    const stepNumber = this.stepNumber;

    // check to make sure labels length is equal to step number
    if (labelsLength === stepNumber) {
      // check amount of label elements -- add or remove accordingly
      let labelElements = this.container?.querySelectorAll('.label') || [];
      const labelElementsLength = labelElements.length || 0;
      const ticks = this.container?.querySelectorAll('.tick') || [];

      if (labelElements && labelElementsLength !== stepNumber) {
        const x = Math.abs(stepNumber - labelElementsLength);
        const labelAttr = !this.disabled ? ' label' : '';

        for (let i = 0; i < x; i++) {
          if (ticks && labelElements && labelElementsLength < stepNumber) {
            ticks[ticks.length - 1 - i]?.insertAdjacentHTML('afterbegin', `<ids-text${labelAttr} class="label"></ids-text>`);
          }
        }
        // grab fresh label elements group
        labelElements = this.container?.querySelectorAll('.label') || [];
      }
      // set the innerHTML for each label in the array

      if (labels && labelsLength === labelElementsLength) {
        labelElements?.forEach((x: { innerHTML: any; classList: { add: (arg0: string) => any; }; }, i: number) => {
          let labelText = this.vertical ? labels[labelsLength - 1 - i] : labels[i];

          if ((labels[0] as any).text) {
            labelText = this.vertical ? (labels[labelsLength - 1 - i] as any).text : (labels[i] as any).text;
          }
          x.innerHTML = labelText;
          if (this.vertical) x?.classList.add('vertical'); // add vertical styles
        });
      }
    } else {
      // set labels to be empty
      const labelElements = this.container?.querySelectorAll('.label');
      labelElements?.forEach((x: { innerHTML: string; }) => {
        x.innerHTML = '';
      });
    }
  }

  /**
   * Helper method for setLabels and initialization of labels
   * @returns {Array} An array the size of stepNumber with numerical intervals between the min and max
   */
  #generateNumericalLabels(): Array<string> {
    const arr = [];
    const stepSize = (this.max - this.min) / (this.stepNumber - 1);

    for (let i = 0; i < this.stepNumber; i++) {
      arr.push(Number((this.min + i * stepSize).toFixed(1)).toString());
    }

    return arr;
  }

  /**
   * Sets the number of steps between start and end slider tick (only applicable to step sliders)
   * @param {string | number | any} value the amount of steps
   */
  set stepNumber(value: string | number | any) {
    if (this.type === 'step') {
      if (parseInt(value) > 0) {
        this.setAttribute(attributes.STEP_NUMBER, value);
      }
    } else {
      this.removeAttribute(attributes.STEP_NUMBER);
    }

    this.#setStepTicks();
  }

  /**
   * @returns {number} the steps between start and end slider tick
   */
  get stepNumber(): number {
    const attrVal = this.getAttribute(attributes.STEP_NUMBER);
    const val: number = attrVal ? parseInt(attrVal, 10) : 0;

    // adding start and end slider ticks
    return val + 2;
  }

  /**
   * Sets the interval between start and end slider tick (only applicable to step sliders)
   * @param {string | number | any} value the amount of steps
   */
  set stepInterval(value: number | string) {
    if (parseInt(value.toString()) > 0) {
      this.setAttribute(attributes.STEP_INTERVAL, value.toString());
      this.stepNumber = Math.ceil((this.max - this.min) / parseInt(value.toString(), 10) - 1);
    } else {
      this.removeAttribute(attributes.STEP_INTERVAL);
      this.stepNumber = 2;
    }

    this.#setStepTicks();
  }

  /**
   * Get the interval between start and end slider tick
   * @returns {number | string} the step interval
   */
  get stepInterval(): number | string {
    return Number(this.getAttribute(attributes.STEP_INTERVAL)) || 'auto';
  }

  #steps: Array<{ value: number; color?: IdsColorValue | string; text?: string }> = [];

  #colorStops: Array<{ value: number; color: IdsColorValue | string }> = [];

  #thresholdSegments: HTMLElement[] = [];

  #isInitialRender = true;

  // Getter for steps attribute
  /**
   * Get the steps array
   * @returns {Array<any>} the steps array
   */
  get steps() {
    return this.#steps;
  }

  /**
   * Setter for steps attribute
   * @param {Array | any} stepsArray an array of steps
   */
  set steps(stepsArray: Array<any> | any) {
    this.#steps = stepsArray;
    this.#setStepTicks();

    // Update color stops when steps change
    if (this.#colorStops.length > 0) {
      if (!this.#isInitialRender) {
        requestAnimationFrame(() => {
          this.updateThresholdSegments();
          this.#updateColor();
        });
      } else {
        this.updateThresholdSegments();
        this.#updateColor();
      }
    }
  }

  /**
   * Get the colorStops array
   * @returns {Array<{ value: number; color: IdsColorValue | string }>} the colorStops array
   */
  get colorStops() {
    return this.#colorStops;
  }

  /**
   * Setter for colorStops attribute - enables color-based theming
   * @param {Array<{ value: number; color: IdsColorValue | string }>} colorStopsArray an array of color stops
   */
  set colorStops(colorStopsArray: Array<{ value: number; color: IdsColorValue | string }>) {
    this.#colorStops = colorStopsArray;
    this.#steps = colorStopsArray.map((stop: { value: number; color: IdsColorValue | string }) => ({
      value: stop.value,
      color: convertColorValue(stop.color),
      text: stop.value.toString()
    }));

    if (this.#colorStops.length > 0) {
      this.progressTrack?.style.setProperty('background-color', 'var(--ids-color-transparent)');
    }

    this.#setStepTicks();
    if (!this.#isInitialRender) {
      requestAnimationFrame(() => {
        this.updateThresholdSegments();
        this.#updateColor();
      });
    } else {
      this.updateThresholdSegments();
      this.#updateColor();
    }
  }

  #setStepTicks() {
    const tickContainer: any = this.container?.querySelector('.tick-container');
    tickContainer.innerHTML = ''; // Clear existing ticks

    // If steps array is provided, use it to set tick positions
    if (this.#steps.length) {
      this.#steps.forEach((step: any) => {
        const tick = document.createElement('span');
        tick.classList.add('tick');

        // Set tick position based on step value
        const positionPercent = ((step.value - this.min) / (this.max - this.min)) * 100;
        if (this.vertical) {
          tick.style.bottom = this.#colorStops.length > 0 ? `${positionPercent}%` : `${100 - positionPercent}%`;
        } else {
          tick.style.insetInlineStart = `${positionPercent}%`;
        }

        // Add label if text is provided
        if (step.text) {
          const label = document.createElement('ids-text');
          label.classList.add('label');
          if (this.vertical) label.classList.add('vertical');
          label.textContent = step.text;
          tick.appendChild(label);
        }

        tickContainer.appendChild(tick);
      });
    } else {
      // Default tick positioning if no steps array is provided
      const stepCount = this.stepNumber;
      for (let i = 0; i < stepCount; i++) {
        const tick = document.createElement('span');
        tick.classList.add('tick');

        // Calculate percentage position for each tick based on step count
        const positionPercent = (i / (stepCount - 1)) * 100;
        if (this.vertical) {
          tick.style.bottom = this.#colorStops.length > 0 ? `${positionPercent}%` : `${100 - positionPercent}%`;
        } else {
          tick.style.insetInlineStart = `${positionPercent}%`;
        }

        tickContainer.appendChild(tick);
      }

      // Generate numerical labels based on min, max, and step count
      this.labels = this.#generateNumericalLabels();
      this.#setStepLabels();
    }

    // Create threshold segments if color stops are enabled
    if (this.#colorStops.length > 0) {
      const updateThresholdFn = () => {
        this.createThresholdSegments();
        this.updateThresholdSegments();
        this.updateThresholdTickColors();
        // Update thumb color when colors change
        const currentThresholdColor = this.getCurrentThresholdColor();
        if (currentThresholdColor) {
          const rgbaColor = colorNameToRgba(currentThresholdColor, 0.1);
          this.thumb?.style.setProperty('background-color', currentThresholdColor);
          this.thumbShadow?.style.setProperty('background-color', rgbaColor);
          this.thumbShadow?.style.setProperty('border', `1px ${currentThresholdColor} solid`);
        }
      };

      if (!this.#isInitialRender) {
        requestAnimationFrame(updateThresholdFn);
      } else {
        updateThresholdFn();
      }
    }
  }

  private isDragging = false;

  /**
   * Sets the secondary slider thumb value based on percentage (range slider only)
   * @param {number | string} value the secondary thumb value as a percentage
   */
  set percentSecondary(value: number | string) {
    this.#percentSecondary = Number(value);
    if (!(this.type === 'step' && this.#colorStops.length > 0 && this.isDragging)) {
      this.#updateProgressBar();
    }
    this.#updateTooltip(this.#calcValueFromPercent(this.#percentSecondary), 'secondary');

    // Ensure thumb position is updated for vertical sliders
    if (this.vertical && this.#colorStops.length > 0) {
      if (!this.#isInitialRender) {
        requestAnimationFrame(() => {
          this.#moveThumb('secondary');
        });
      }
    }
  }

  /**
   * @returns {number} the secondary thumb value as a percentage (range slider only)
   */
  get percentSecondary(): number {
    // we need all these checks so that it still works with 0
    if (Number.isNaN(this.#percentSecondary) || typeof this.#percentSecondary === 'undefined' || this.#percentSecondary === null || (this.#percentSecondary as any) === '') {
      // calculate on the fly if not a valid number
      return ((this.valueSecondary - this.min) / (this.max - this.min)) * 100;
    }
    return this.#percentSecondary;
  }

  /**
   * Sets the primary slider thumb value based on percentage
   * @param {number | string} value the secondary thumb value as a percentage
   */
  set percent(value: number | string) {
    this.#percent = Number(value);
    if (!(this.type === 'step' && this.#colorStops.length > 0 && this.isDragging)) {
      this.#updateProgressBar();
    }
    this.#updateTooltip(this.#calcValueFromPercent(this.#percent));

    // Ensure thumb position is updated for vertical sliders
    if (this.vertical && this.#colorStops.length > 0) {
      if (!this.#isInitialRender) {
        requestAnimationFrame(() => {
          this.#moveThumb('primary');
        });
      }
    }
  }

  /**
   * @returns {number} the primary thumb value as a percentage
   */
  get percent(): number {
    if (Number.isNaN(this.#percent) || typeof this.#percent === 'undefined' || this.#percent === null || (this.#percent as any) === '') {
      return ((this.value - this.min) / (this.max - this.min)) * 100;
    }
    return this.#percent;
  }

  /**
   * Sanitizes a value to be applied to the slider, and doesn't allow the value
   * to pass beyond the min/max values.
   * @private
   * @param {string | number | any} value incoming value to set
   * @param {boolean} secondary true if this value represents the secondary slider thumb (range slider only)
   * @returns {number} the corrected slider number
   */
  #sanitizeValue(value: string | number | any, secondary?: boolean): number {
    const fixedValue = Math.round(parseFloat(value));
    if (fixedValue <= this.min) {
      return this.min;
    }
    if (fixedValue >= this.max) {
      return this.max;
    }
    if (this.type === 'range') {
      if (!secondary && fixedValue >= this.valueSecondary) {
        return this.valueSecondary;
      }
      if (secondary && fixedValue <= this.value) {
        return this.value;
      }
    }
    return fixedValue;
  }

  /**
   * Set the secondary value of the slider (range slider only)
   * @param {string} value The secondary input value
   */
  set valueSecondary(value: string | number | any) {
    if (this.readonly || this.disabled) return;

    const newValue = this.#sanitizeValue(value, true);

    this.#isSettingAttribute = true;
    this.setAttribute(attributes.VALUE_SECONDARY, `${newValue}`);

    // Update percent and ensure UI updates for vertical sliders
    const newPercent = ((newValue - this.min) / (this.max - this.min)) * 100;
    this.#percentSecondary = newPercent;

    this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUENOW, `${newValue}`);
    this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUETEXT, `${newValue}`);
    if (this.type === 'range') {
      this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUEMIN, `${newValue}`);
    }
    this.#updateTooltip(newValue, 'secondary');

    // Force UI update for vertical sliders
    if (this.#colorStops.length > 0 && !this.#isInitialRender) {
      requestAnimationFrame(() => {
        this.#moveThumb('secondary');
        this.#updateProgressBar();
      });
    } else {
      this.#moveThumb('secondary');
      this.#updateProgressBar();
    }

    if (!this.#isSettingAttribute) {
      this.#triggerChangeEvent(newValue, 'secondary');
    }

    this.#isSettingAttribute = false;
  }

  /**
   * @returns {number} the secondary slider value (range slider only)
   */
  get valueSecondary(): number {
    const b = this.getAttribute(attributes.VALUE_SECONDARY);
    if (b === null || b === '' || Number.isNaN(b)) {
      return this.max;
    }

    return parseFloat(this.getAttribute(attributes.VALUE_SECONDARY) ?? '');
  }

  /**
   * Set the primary value of the slider
   * @param {string} value The primary input value
   */
  set value(value: string | number | any) {
    if (this.readonly || this.disabled) return;

    const newValue = this.#sanitizeValue(value);

    this.#isSettingAttribute = true;
    this.setAttribute(attributes.VALUE, `${newValue}`);

    // Update percent and ensure UI updates for vertical sliders
    const newPercent = ((newValue - this.min) / (this.max - this.min)) * 100;
    this.#percent = newPercent;

    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUENOW, `${newValue}`);
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUETEXT, `${newValue}`);
    if (this.type === 'range') {
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUEMIN, `${newValue}`);
    }
    this.#updateTooltip(newValue, 'primary');

    // Force UI update for vertical sliders
    if (this.#colorStops.length > 0 && !this.#isInitialRender) {
      requestAnimationFrame(() => {
        this.#moveThumb('primary');
        this.#updateProgressBar();
      });
    } else {
      this.#moveThumb('primary');
      this.#updateProgressBar();
    }

    // Update threshold segments if color stops are enabled
    if (this.#colorStops.length > 0) {
      this.updateThresholdSegments();
      // Update thumb color based on current threshold
      const currentThresholdColor = this.getCurrentThresholdColor();
      if (currentThresholdColor) {
        const rgbaColor = colorNameToRgba(currentThresholdColor, 0.1);
        this.thumb?.style.setProperty('background-color', currentThresholdColor);
        this.thumbShadow?.style.setProperty('background-color', rgbaColor);
        this.thumbShadow?.style.setProperty('border', `1px ${currentThresholdColor} solid`);
      }
    }

    if (!this.#isSettingAttribute) {
      this.#triggerChangeEvent(newValue, 'primary');
    }

    this.#isSettingAttribute = false;
  }

  /**
   * @returns {number} the primary slider value
   */
  get value(): number {
    const a = this.getAttribute(attributes.VALUE);
    if (a === null || a === '' || Number.isNaN(a)) {
      return this.min;
    }
    return parseFloat((this.getAttribute(attributes.VALUE) ?? ''));
  }

  /**
   * Gets the formatted value according to current locale settings,
   * preserving the original number of decimal places.
   * @returns {number|string} The localized number with original precision
   */
  get localizeValue(): number | string {
    const value = this.value;
    const getFractionDigits = (number: number) => {
      const parts = number.toString().split('.');
      return parts[1]?.length || 0;
    };
    return this.localeAPI.formatNumber(value, {
      minimumFractionDigits: getFractionDigits(value)
    });
  }

  /**
   * Triggers a Change Event
   * @param {string | number | any } value the value of the slider handle
   * @param {string} [thumb] the slider thumb causing the change
   */
  #triggerChangeEvent(value: string | number | any, thumb?: undefined | string): void {
    this.triggerEvent('change', this, {
      bubbles: true,
      detail: {
        elem: thumb === 'secondary' ? this.thumbSecondaryDraggable : this.thumbDraggable,
        percent: thumb === 'secondary' ? this.percentSecondary : this.percent,
        value
      }
    });
  }

  /**
   * Sets the minimum-possible value of the slider
   * @param {string} value The desired minimum
   */
  set min(value: string | number | any) {
    const val: any = parseFloat(value);
    if (val >= this.max || val === null || val === '' || Number.isNaN(val)) {
      this.setAttribute(attributes.MIN, String(DEFAULT_MIN));
    } else {
      this.setAttribute(attributes.MIN, val);
    }

    this.#refreshUI();
  }

  /**
   * @returns {number} the minimum value possible that can be set on slider thumbs
   */
  get min(): number {
    return parseFloat(this.getAttribute(attributes.MIN) ?? '') || DEFAULT_MIN;
  }

  /**
   * Sets the maximum-possible value of the slider
   * @param {string} value The desired max
   */
  set max(value: string | number | any) {
    const val: any = parseFloat(value);
    if (val <= this.min || val === null || val === '' || Number.isNaN(val)) {
      this.setAttribute(attributes.MAX, String(this.min + DEFAULT_MAX));
    } else {
      this.setAttribute(attributes.MAX, val);
    }

    this.#refreshUI();
  }

  /**
   * @returns {number} the maximum value possible that can be set on slider thumbs
   */
  get max(): number {
    const val: any = parseFloat(this.getAttribute(attributes.MAX) ?? '');
    if (val <= this.min || val === null || val === '' || Number.isNaN(val)) {
      return DEFAULT_MAX;
    }
    return val;
  }

  /**
   * Sets the slider type
   * @param {IdsSliderType} value The type of slider
   */
  set type(value: IdsSliderType) {
    if (value && ['step', 'range', 'single'].includes(value)) {
      this.setAttribute(attributes.TYPE, value);
    } else {
      this.setAttribute(attributes.TYPE, DEFAULT_TYPE);
    }
    this.container?.classList[value === 'range' ? 'add' : 'remove']('range');
  }

  /**
   * @returns {IdsSliderType} the slider type
   */
  get type(): IdsSliderType {
    return (this.getAttribute(attributes.TYPE) || DEFAULT_TYPE) as IdsSliderType;
  }

  /**
   * Enables a tooltip displaying thumb values when either thumb is focused
   * @param {boolean | string} value true if the thumb should display tooltips
   */
  set showTooltip(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.TOOLTIP, `${value}`);
    } else {
      this.removeAttribute(attributes.TOOLTIP);
    }
  }

  /**
   * Get whether tooltips are shown
   * @returns {boolean | string} true if tooltips are shown
   */
  get showTooltip(): boolean | string {
    return this.hasAttribute(attributes.TOOLTIP);
  }

  /**
   * Set the color of the bar
   * @param {string} value The color, this can be a hex code with the #, a native css color, or an ids-status color
   */
  set color(value: string) {
    this.setAttribute(attributes.COLOR, value);
    if (this.#colorStops.length > 0 && !this.#isInitialRender) {
      requestAnimationFrame(() => {
        this.#updateColor();
      });
    } else {
      this.#updateColor();
    }
  }

  /**
   * Get the color of the bar
   * @returns {string} the color value
   */
  get color(): string { return this.getAttribute(attributes.COLOR) || ''; }

  /**
   * @private
   * @returns {boolean} true if this slider should have a custom color applied
   */
  #shouldApplyColor(): boolean {
    return !this.readonly && !this.disabled;
  }

  /**
   * Create threshold segments for visual coloring
   * @private
   */
  private createThresholdSegments(): void {
    if (!(this.#colorStops.length > 0) || !this.#steps.length) return;

    const track = this.container?.querySelector('.track');
    if (!track) return;

    // Remove existing threshold segments
    this.#thresholdSegments.forEach((segment) => segment.remove());
    this.#thresholdSegments = [];

    for (let i = 0; i < this.#steps.length - 1; i++) {
      const currentStep = this.#steps[i];
      const nextStep = this.#steps[i + 1];

      if (currentStep.color) {
        const segmentDiv = document.createElement('div');
        segmentDiv.className = 'threshold-segment';

        const startPercent = ((currentStep.value - this.min) / (this.max - this.min)) * 100;
        const endPercent = ((nextStep.value - this.min) / (this.max - this.min)) * 100;

        segmentDiv.style.setProperty('position', 'absolute');
        segmentDiv.style.setProperty('background-color', currentStep.color);
        segmentDiv.style.setProperty('z-index', '1');
        segmentDiv.style.setProperty('opacity', '0.3');

        if (this.vertical) {
          segmentDiv.style.setProperty('width', '100%');
          segmentDiv.style.setProperty('bottom', `${startPercent}%`);
          segmentDiv.style.setProperty('height', `${endPercent - startPercent}%`);
        } else {
          segmentDiv.style.setProperty('height', '100%');
          segmentDiv.style.setProperty('inset-inline-start', `${startPercent}%`);
          segmentDiv.style.setProperty('width', `${endPercent - startPercent}%`);
        }

        track.appendChild(segmentDiv);
        this.#thresholdSegments.push(segmentDiv);
      }
    }
  }

  /**
   * Update threshold segments for a specific value
   * @private
   * @param {number} value The value to update segments for
   */
  private updateThresholdSegmentsForValue(value: number): void {
    if (!(this.#colorStops.length > 0) || !this.#thresholdSegments.length) return;

    this.#thresholdSegments.forEach((segment, index) => {
      const currentStep = this.#steps[index];
      const nextStep = this.#steps[index + 1];

      const startPercent = ((currentStep.value - this.min) / (this.max - this.min)) * 100;
      const endPercent = ((nextStep.value - this.min) / (this.max - this.min)) * 100;

      if (this.vertical) {
        // Reset to original height for vertical
        segment.style.height = `${endPercent - startPercent}%`;
        segment.style.opacity = '0';

        if (value >= nextStep.value) {
          segment.style.opacity = '1';
        } else if (value > currentStep.value) {
          const progress = (value - currentStep.value) / (nextStep.value - currentStep.value);
          segment.style.opacity = '1';
          segment.style.height = `${(endPercent - startPercent) * progress}%`;
        }

        return;
      }
      // Reset to original width for horizontal
      segment.style.width = `${endPercent - startPercent}%`;
      segment.style.opacity = '0';

      if (value >= nextStep.value) {
        segment.style.opacity = '1';
      } else if (value > currentStep.value) {
        const progress = (value - currentStep.value) / (nextStep.value - currentStep.value);
        segment.style.opacity = '1';
        segment.style.width = `${(endPercent - startPercent) * progress}%`;
      }
    });
  }

  /**
   * Update threshold segments based on current value
   * @private
   */
  private updateThresholdSegments(): void {
    this.updateThresholdSegmentsForValue(this.value);
  }

  /**
   * Updates the thumb and tick colors on the slider based on the `color` attribute and other settings
   * @private
   */
  #updateColor(): void {
    let color;
    if (this.#shouldApplyColor()) {
      color = this.color;
    }

    const ticks = this.container?.querySelectorAll<HTMLElement>('.tick') || [];

    if (color && !(this.#colorStops.length > 0)) {
      let colorString = color;
      if (color.substring(0, 1) !== '#') {
        colorString = statusToIDSColor(color);
      }
      const rgbaColor = colorNameToRgba(colorString, 0.1);

      ticks?.forEach((tick: { children: HTMLCollection, style: CSSStyleDeclaration }) => {
        tick?.style.setProperty('background-color', colorString);
        tick?.children[0]?.setAttribute('label', '');
      });
      this.thumb?.style.setProperty('background-color', colorString);
      this.thumbShadow?.style.setProperty('background-color', rgbaColor);
      this.thumbShadow?.style.setProperty('border', `1px ${colorString} solid`);
      this.progressTrack?.style.setProperty('background-color', colorString);
      if (this.type === 'range' && this.thumbShadowSecondary && this.thumbSecondary) {
        this.thumbShadowSecondary?.style.setProperty('background-color', rgbaColor);
        this.thumbShadowSecondary?.style.setProperty('border', `1px ${colorString} solid`);
        this.thumbSecondary?.style.setProperty('background-color', colorString);
      }
    } else if (this.#colorStops.length > 0) {
      // Handle threshold coloring for ticks
      this.updateThresholdTickColors();

      // Set thumb color based on current value and thresholds
      const currentThresholdColor = this.getCurrentThresholdColor();
      if (currentThresholdColor) {
        const rgbaColor = colorNameToRgba(currentThresholdColor, 0.1);
        if (!this.disabled) this.thumb?.style.setProperty('background-color', currentThresholdColor);
        this.thumbShadow?.style.setProperty('background-color', rgbaColor);
        this.thumbShadow?.style.setProperty('border', `1px ${currentThresholdColor} solid`);
      }
    } else {
      ticks?.forEach((tick: { children: HTMLCollection, style: CSSStyleDeclaration }) => {
        tick?.style.removeProperty('background-color');
        if (!this.readonly) {
          tick.children[0]?.removeAttribute('label');
        } else {
          tick.children[0]?.setAttribute('label', '');
        }
      });
      this.thumb?.style.removeProperty('background-color');
      this.thumbShadow?.style.removeProperty('background-color');
      this.thumbShadow?.style.removeProperty('border');
      this.progressTrack?.style.removeProperty('background-color');
      if (this.type === 'range' && this.thumbShadowSecondary && this.thumbSecondary) {
        this.thumbShadowSecondary?.style.removeProperty('background-color');
        this.thumbShadowSecondary?.style.removeProperty('border');
        this.thumbSecondary?.style.removeProperty('background-color');
      }
    }
  }

  /**
   * Update tick colors for threshold coloring
   * @private
   */
  private updateThresholdTickColors(): void {
    const ticks = this.container?.querySelectorAll<HTMLElement>('.tick') || [];
    ticks.forEach((tick, index) => {
      const stepColor = this.#steps[index]?.color;
      if (stepColor) {
        tick.style.setProperty('background-color', stepColor, 'important');
      }

      if (this.disabled) tick.style.removeProperty('background-color');
    });

    // Handle threshold segments for disabled state
    this.#thresholdSegments.forEach((segment) => {
      if (this.disabled) {
        segment.style.removeProperty('background-color');
      }
    });

    if (this.disabled && this.thumb) this.thumb.style.removeProperty('background-color');
  }

  /**
   * Get threshold color for a specific value
   * @private
   * @param {number} value The value to get color for
   * @returns {string | null} The color for the threshold
   */
  private getThresholdColorForValue(value: number): string | null {
    if (!this.#steps || this.#steps.length === 0) {
      return null;
    }

    // Find the appropriate threshold range for the value
    for (let i = 1; i < this.#steps.length; i++) {
      if (value <= this.#steps[i].value) {
        return this.#steps[i - 1]?.color || null;
      }
    }

    // If value exceeds all thresholds, return the last step's color
    return this.#steps[this.#steps.length - 1]?.color || null;
  }

  /**
   * Get current threshold color based on slider value
   * @private
   * @returns {string | null} The color for the current threshold
   */
  private getCurrentThresholdColor(): string | null {
    return this.getThresholdColorForValue(this.value);
  }

  /**
   * Hide/show the tooltip of the value
   * @private
   * @param {boolean} hide whether or not to hide it
   * @param {primaryOrSecondary} primaryOrSecondary which tooltip to hide
   */
  #updateTooltipDisplay(hide: boolean, primaryOrSecondary?: string): void {
    if (!this.showTooltip) {
      hide = true;
    }
    if (primaryOrSecondary === 'secondary' && this.tooltipSecondary) {
      this.tooltipSecondary.style.setProperty('opacity', hide ? '0' : '1');
    } else {
      this.tooltip?.style.setProperty('opacity', hide ? '0' : '1');
    }
  }

  /**
   * Hide/show the spotlight/box-shadow of the thumb
   * @private
   * @param {boolean} hide whether or not to hide it
   * @param {string} primaryOrSecondary which thumb to hide
   */
  #updateThumbShadow(hide: boolean, primaryOrSecondary: string): void {
    let thumbShadow = this.thumbShadow;

    if (primaryOrSecondary === 'secondary' && this.thumbShadowSecondary) {
      thumbShadow = this.thumbShadowSecondary;
    }

    if (hide) {
      thumbShadow?.classList.remove('active');
    } else {
      thumbShadow?.classList.add('active');
    }
  }

  /**
   * Helper method to calculate the percentage of slider from mouse click; not a pure function
   * @private
   * @param {number} x coordinate of mouse click
   * @param {number} y coordinate of mouse click
   * @returns {number} the percent
   */
  #calcPercentFromClick(x: number, y: number): number {
    this.#refreshTrackBounds();
    const {
      TOP,
      BOTTOM,
      LEFT,
      RIGHT
    } = this.#trackBounds;

    const mousePos = this.vertical ? y : x;
    const horizontalStart = this.isRTL ? RIGHT : LEFT;
    const horizontalEnd = this.isRTL ? LEFT : RIGHT;
    const startPos = this.vertical ? BOTTOM : horizontalStart;
    const endPos = this.vertical ? TOP : horizontalEnd;

    const percent = this.#calcPercentFromRange(mousePos, startPos, endPos);

    return percent;
  }

  /**
   * Perform the calculations to update the UI and value(s)/percent(s) accordingly
   * @private
   * @param {number} x coordinate of mouse click
   * @param {number} y coordnate of mouse click
   * @param {number} labelValueClicked if label was clicked or not
   * @param {string} primaryOrSecondary string representing the primary/secondary label for range sliders
   */
  #calculateUIFromClick(x: number, y: number, labelValueClicked?: number, primaryOrSecondary?: string): void {
    if (this.type !== 'step') {
      let value = labelValueClicked ?? this.#calcValueFromPercent(this.#calcPercentFromClick(x, y));

      const thumbPos = this.vertical
        ? this.thumbDraggable?.getBoundingClientRect().y
        : this.thumbDraggable?.getBoundingClientRect().x;

      let thumbDraggable = this.thumbDraggable;
      let valueAttribute = 'value';

      if (this.type === 'range') {
        const thumbPosSecondary = this.vertical
          ? this.thumbDraggableSecondary?.getBoundingClientRect().y
          : this.thumbDraggableSecondary?.getBoundingClientRect().x;

        // figure out which thumb is closer to the click location
        const mousePos = this.vertical ? y : x;

        if (Math.abs(mousePos - (thumbPos ?? NaN)) > Math.abs(mousePos - (thumbPosSecondary ?? NaN))) {
          thumbDraggable = this.thumbDraggableSecondary;
          valueAttribute = 'valueSecondary';
          primaryOrSecondary = 'secondary';
        }

        // Correct the placement of the thumb if it goes beyond the opposite thumb's value
        if (primaryOrSecondary === 'primary' && value > this.valueSecondary) {
          value = (this.valueSecondary - 1);
        } else if (primaryOrSecondary === 'secondary' && value < this.value) {
          value = (this.value + 1);
        }
      }

      if (value !== (this as any)[valueAttribute]) {
        (this as any)[valueAttribute] = value;
      } else {
        this.#moveThumb(primaryOrSecondary);
        this.#triggerChangeEvent(value, primaryOrSecondary);
      }

      thumbDraggable?.focus();
    } else {
      // Calculate step size based on min, max, and step interval
      const stepSize = (this.max - this.min) / (this.stepNumber - 1);
      const halfStepThreshold = stepSize / 2;

      // Determine the passed value based on click position
      const passedValue = labelValueClicked || this.#calcValueFromPercent(this.#calcPercentFromClick(x, y));

      // Find the closest tick and check if click is within half-step threshold
      let closestValue = this.ticks[0].value;
      let minDifference = Math.abs(this.ticks[0].value - passedValue);

      for (let i = 1; i < this.ticks.length; i++) {
        const tickValue = this.ticks[i].value;
        const difference = Math.abs(tickValue - passedValue);

        // Update closest tick if within the threshold
        if (difference < minDifference && difference <= halfStepThreshold) {
          closestValue = tickValue;
          minDifference = difference;
        }
      }

      // Set slider to closest tick if it's within half-step threshold
      this.percent = ((closestValue - this.min) / (this.max - this.min)) * 100;
      if (closestValue !== this.value) {
        this.value = closestValue;
      } else {
        this.#moveThumb('primary');
        this.#triggerChangeEvent(closestValue, 'primary');
      }

      // Update threshold segments for step sliders
      if (this.#colorStops.length > 0) {
        this.updateThresholdSegments();
      }

      this.thumbDraggable?.focus();
    }
  }

  /**
   * Translate the thumb(s) according to the percent values
   * @private
   * @param {string} primaryOrSecondary which thumb to move
   */
  #moveThumb(primaryOrSecondary?: string) {
    this.#refreshTrackBounds();

    let thumbDraggable = this.thumbDraggable;
    let percent = this.percent;

    // secondary values
    if (primaryOrSecondary === 'secondary' && this.type === 'range') {
      thumbDraggable = this.thumbDraggableSecondary;
      percent = this.percentSecondary;
    }

    const {
      TOP,
      BOTTOM,
      LEFT,
      RIGHT
    } = this.#trackBounds;

    const startPos = this.vertical ? BOTTOM : LEFT;
    const endPos = this.vertical ? TOP : RIGHT;
    const centered = true;
    let trans = this.#calcTranslateFromPercent(startPos, endPos, percent, centered);

    if (this.vertical || this.isRTL) { trans *= -1; }
    const transString = this.vertical ? `translate(0, ${trans}px)` : `translate(${trans}px, 0)`;
    thumbDraggable?.style.setProperty('transform', transString);
  }

  /**
   * Calculate the true value based on the percent value
   * @private
   * @param {number} percent the percent value to convert to numerical value btw min and max
   * @returns {number} the calculated value
   */
  #calcValueFromPercent(percent: number): number {
    return ((percent / 100) * (this.max - this.min)) + (this.min);
  }

  /**
   * Calculate the pixels to translate thumb(s) or progress track based on percent value
   * @private
   * @param {number} nStart the starting x or y coordinate of the slider
   * @param {number} nEnd the ending x or y coordinate of the slider
   * @param {number} percent the percent/location of the thumb relative to the slider
   * @param {boolean} centered whether or not the thumb(s) or progress track is centered or not
   * if notCentered is true, it will translate from 0
   * if notCentered is false, it will translate negatively and positively with 0 being the center
   * @returns {number} coordinates or the amount of pixels to translate by
   */
  #calcTranslateFromPercent(nStart: number, nEnd: number, percent: number, centered: boolean): number {
    // minus thumb height bc it overshoots
    const editedRange = Math.abs(nEnd - nStart) - (this.thumbDraggable?.clientWidth || 0);
    // if (this.stepNumber <= 10 && this.percent > this.min
    //  && this.percent < this.max) editedRange += (this.thumbDraggable?.clientWidth || 0);
    let coord = (percent / 100) * editedRange;
    coord = centered ? coord - (editedRange / 2) : coord;
    return coord;
  }

  /**
   * Calculate the percent value based on mouse click location
   * @private
   * @param {number} n the mouse x or y coordinate
   * @param {number} nStart the starting x or y coordinate of the slider
   * @param {number} nEnd the ending x or y coordinate of the slider
   * @returns {number} the percent/location of the thumb relative to the slider
   */
  #calcPercentFromRange(n: number, nStart: number, nEnd: number): number {
    const thumbWidth = this.thumbDraggable?.clientWidth ?? NaN;
    let percent = 0;
    // allow bigger hit areas for controlling thumb
    const range = Math.abs(nStart - nEnd) - thumbWidth / 2;
    const endDelta = Math.abs(n - nEnd);
    const startDelta = Math.abs(n - nStart);
    if (endDelta > range) {
      percent = 0;
    } else if (startDelta > range) {
      percent = 100;
    } else {
      percent = ((startDelta - thumbWidth / 2) / (range - thumbWidth / 2)) * 100;
    }
    return percent;
  }

  /**
   * Attaches all necessary event listeners
   * @private
   */
  #attachEventListeners(): void {
    this.#attachResizeObserver();
    this.#attachDragEventListeners();
    if (this.type === 'range') this.#attachDragEventListeners('secondary');
    this.#attachFocusListeners();
    this.#attachKeyboardListeners();
    this.#attachClickListeners();
  }

  /**
   * Calculates the x,y coordinates of the bounding box of the clickable track area
   * @private
   * @returns {IdsSliderTrackBounds} The track area boundaries
   */
  #calculateTrackBounds(): IdsSliderTrackBounds {
    if (!this.trackArea) return DEFAULT_TRACKER_BOUNDS;

    const rect = this.trackArea.getBoundingClientRect();
    const LEFT = rect.left + window.scrollX;
    const TOP = rect.top + window.scrollY;
    const RIGHT = LEFT + this.trackArea.clientWidth;
    const BOTTOM = TOP + this.trackArea.clientHeight;

    const bounds = {
      LEFT,
      RIGHT,
      TOP,
      BOTTOM,
    };

    return bounds;
  }

  /**
   * @private
   * @param {boolean} toggleOn true if slider elements should be animated
   */
  #toggleAnimations(toggleOn: boolean): void {
    this.container?.classList[toggleOn ? 'add' : 'remove']('animated');
  }

  /**
   * Sets initial slider styles and element placement
   * @private
   */
  #attachUIStyles(): void {
    // init UI styles
    this.#updateProgressBar();
    this.#moveThumb();
    if (this.type === 'range') this.#moveThumb('secondary');
    this.#updateColor();

    // init base min/max labels
    if (this.firstTick && this.lastTick) {
      const maxTick = this.vertical ? this.firstTick : this.lastTick;
      const minTick = this.vertical ? this.lastTick : this.firstTick;

      minTick.innerHTML = `<ids-text label class="label${this.vertical ? ' vertical' : ''}">${this.min}</ids-text>`;
      maxTick.innerHTML = `<ids-text label class="label${this.vertical ? ' vertical' : ''}">${this.max}</ids-text>`;
    }
  }

  #refreshUI(): void {
    // Recalculate and sanitize current value based on new min/max
    const currentValue = this.value;
    const sanitizedValue = this.#sanitizeValue(currentValue);

    // Update value if it changed due to sanitization
    if (sanitizedValue !== currentValue) {
      this.#isSettingAttribute = true;
      this.setAttribute(attributes.VALUE, `${sanitizedValue}`);
      this.#isSettingAttribute = false;
    }

    // Recalculate percent based on new min/max range
    this.#percent = ((sanitizedValue - this.min) / (this.max - this.min)) * 100;

    // Update secondary value for range sliders
    let sanitizedValueSecondary = this.valueSecondary;
    if (this.type === 'range') {
      const currentValueSecondary = this.valueSecondary;
      sanitizedValueSecondary = this.#sanitizeValue(currentValueSecondary, true);

      if (sanitizedValueSecondary !== currentValueSecondary) {
        this.#isSettingAttribute = true;
        this.setAttribute(attributes.VALUE_SECONDARY, `${sanitizedValueSecondary}`);
        this.#isSettingAttribute = false;
      }

      this.#percentSecondary = ((sanitizedValueSecondary - this.min) / (this.max - this.min)) * 100;
    }

    // Recalculate stepNumber if stepInterval is set
    if (this.type === 'step' && this.stepInterval !== 'auto') {
      const intervalValue = parseInt(this.stepInterval.toString(), 10);
      if (intervalValue > 0) {
        const numIntervals = Math.ceil((this.max - this.min) / intervalValue);
        this.setAttribute(attributes.STEP_NUMBER, (numIntervals - 1).toString());
      }
    }

    this.#setStepTicks();
    this.#attachUIStyles();
  }

  /**
   * Attaches ARIA attributes to some slider elements
   * @private
   */
  #attachARIA(): void {
    this.setAttribute(htmlAttributes.ROLE, 'none');
    this.thumbDraggable?.setAttribute(htmlAttributes.ROLE, 'slider');
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_ORIENTATION, this.vertical ? 'vertical' : 'horizontal');
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUEMIN, `${this.min}`);
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUEMAX, `${this.max}`);
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUENOW, `${this.value}`);
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_VALUETEXT, `${this.value}`);
    this.thumbDraggable?.setAttribute(htmlAttributes.ARIA_LABEL, `${this.ariaLabel}`);

    if (this.type === 'range') {
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ROLE, 'slider');
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_ORIENTATION, this.vertical ? 'vertical' : 'horizontal');
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUEMIN, `${this.min}`);
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUEMAX, `${this.max}`);
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUENOW, `${this.valueSecondary}`);
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_VALUETEXT, `${this.valueSecondary}`);
      this.thumbDraggableSecondary?.setAttribute(htmlAttributes.ARIA_LABEL, `${this.ariaLabelSecondary}`);
    }
  }

  /**
   * Recalculates and updates the track bounds
   * @private
   */
  #refreshTrackBounds(): void {
    this.#trackBounds = this.#calculateTrackBounds();
  }

  /**
   * Checks if the window changes sizes and updates UI accordingly
   * @private
   */
  #attachResizeObserver(): void {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          this.#refreshTrackBounds();
          this.#updateProgressBar();
          this.#moveThumb();
          if (this.type === 'range') this.#moveThumb('secondary');
        }
      }
    });

    if (this.trackArea) resizeObserver.observe(this.trackArea);
  }

  /**
   * Add event listeners for clicking the track area
   * @private
   */
  #attachClickListeners(): void {
    this.onEvent('click', this.container, (event: { target: { className: any; innerHTML: string; }; clientX: number; clientY: number; }) => {
      if (this.disabled || this.readonly) {
        return;
      }

      const className = event.target.className;
      const clickedSlider = className.includes('ids-slider');
      const clickedLabel = className.includes('label');
      const clickedTrackArea = className.includes('track-area');

      if (clickedSlider || clickedLabel || clickedTrackArea) {
        if (clickedTrackArea || clickedSlider) {
          this.#calculateUIFromClick(event.clientX, event.clientY);
        } else {
          let labelValueClicked = parseFloat(event.target.innerHTML);
          if (Number.isNaN(labelValueClicked)) {
            const index = Array.from((event.target as any).parentElement.parentNode.children)
              .indexOf((event.target as any).parentElement);

            labelValueClicked = this.vertical
              ? (this.labels[this.labels.length - 1 - index] as any).value : (this.labels[index] as any)?.value;
          }
          this.#calculateUIFromClick(event.clientX, event.clientY, labelValueClicked);
        }
      }
    });

    this.onEvent('click', document, (event: any) => {
      if (event.target !== this && !this.disabled) {
        this.#updateTooltipDisplay(true);
        if (this.type === 'range') this.#updateTooltipDisplay(true, 'secondary');
      }
    });
  }

  /**
   * Add event listeners for dragging the slider thumbs
   * @private
   * @param {string} primaryOrSecondary the primary or secondary thumb
   */
  #attachDragEventListeners(primaryOrSecondary?: string): void {
    const d = this.type === 'range' && primaryOrSecondary === 'secondary';
    const obj = {
      thumbDraggable: d ? this.thumbDraggableSecondary : this.thumbDraggable,
      thumbDraggableOther: d ? this.thumbDraggable : this.thumbDraggableSecondary,
      primaryOrSecondary: d ? 'secondary' : 'primary',
      valueAttribute: d ? 'valueSecondary' : 'value',
      percentAttribute: d ? 'percentSecondary' : 'percent'
    };

    const swapZIndex = () => {
      if (obj.thumbDraggableOther) {
        obj.thumbDraggableOther?.style.setProperty('z-index', '50');
        obj.thumbDraggable?.style.setProperty('z-index', '51');
      }
    };

    // Listen for drag event on draggable thumb
    this.onEvent('drag', obj.thumbDraggable, (e: { detail: { mouseX: any; mouseY: any; }; }) => {
      if (this.type !== 'step') this.#updateTooltipDisplay(false);

      const [x, y] = [e.detail.mouseX, e.detail.mouseY];
      const percent = this.#calcPercentFromClick(x, y);

      if (this.type === 'range') swapZIndex();
      // only set the percent--because changing the value causes the moveThumb() to fire like crazy
      (this as any)[obj.percentAttribute] = percent;

      // Update thumb color and segments during drag if color stops are enabled
      if (this.#colorStops.length > 0 && obj.primaryOrSecondary === 'primary') {
        const dragValue = this.#calcValueFromPercent(percent);
        const currentThresholdColor = this.getThresholdColorForValue(dragValue);
        if (currentThresholdColor) {
          const rgbaColor = colorNameToRgba(currentThresholdColor, 0.1);
          this.thumb?.style.setProperty('background-color', currentThresholdColor);
          this.thumbShadow?.style.setProperty('background-color', rgbaColor);
          this.thumbShadow?.style.setProperty('border', `1px ${currentThresholdColor} solid`);
        }
        this.updateThresholdSegmentsForValue(dragValue);
      }

      // Expose this event externally
      this.triggerEvent('ids-slider-drag', this, {
        bubbles: true,
        detail: {
          elem: obj.thumbDraggable,
          mouseX: e.detail.mouseX,
          mouseY: e.detail.mouseY,
          percent,
          value: this.#calcValueFromPercent(percent)
        }
      });
    });

    this.onEvent('dragstart', obj.thumbDraggable, () => {
      this.isDragging = true;
      this.#toggleAnimations(false);
      this.#updateThumbShadow(true, obj.primaryOrSecondary);
      this.#updateThumbShadow(true, obj.primaryOrSecondary === 'secondary' ? 'primary' : 'secondary');
    });

    this.onEvent('dragend', obj.thumbDraggable, (e: CustomEvent) => {
      const wasDragging = this.isDragging;
      this.isDragging = false;
      this.#toggleAnimations(true);
      obj.thumbDraggable?.focus();
      // to ensure that after dragging, the value is updated only after dragging has ended..
      // this is the roundabout solution to prevent the firing of moveThumb() every ids-drag event
      let labelValue: any = obj.primaryOrSecondary === 'secondary' ? this.percentSecondary : (this.percent * (this.max - this.min)) / 100;
      if (this.type === 'step') labelValue = undefined;
      if (this.type === 'range' && obj.primaryOrSecondary === 'secondary') this.valueSecondary = labelValue;
      else if (this.type === 'range' && obj.primaryOrSecondary === 'primary') this.value = labelValue;
      if (!this.vertical || this.type === 'step') this.#calculateUIFromClick(e.detail.mouseX, e.detail.mouseY, labelValue, obj.primaryOrSecondary);
      this.#updateThumbShadow(false, obj.primaryOrSecondary);
      if (wasDragging && this.type === 'step' && this.#colorStops.length > 0) {
        this.#updateProgressBar();
      }
    });
  }

  /**
   * Add event listeners for arrow keys to move thumbs
   * @private
   */
  #attachKeyboardListeners(): void {
    this.onEvent('keydown', this, (event: { code: string; preventDefault: () => void; target: { name: string; }; key: any; }) => {
      if (this.readonly || this.disabled) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(event.code) > -1) {
        event.preventDefault();

        // Disables animation on non-step sliders when using keys
        if (this.type !== 'step') this.#toggleAnimations(false);
      }

      if (event.target.name === 'ids-slider') {
        let primaryOrSecondary = '';

        if (this.type === 'range') {
          // check if focus is on b or a
          if (this.thumbShadowSecondary?.classList.contains('active')) {
            primaryOrSecondary = 'secondary';
          }
        }

        switch (event.key) {
          case 'ArrowUp':
            this.#increaseValue(primaryOrSecondary);
            break;
          case 'ArrowDown':
            this.#decreaseValue(primaryOrSecondary);
            break;
          case 'ArrowRight':
            if (this.isRTL) this.#decreaseValue(primaryOrSecondary);
            else this.#increaseValue(primaryOrSecondary);
            break;
          case 'ArrowLeft':
            if (this.isRTL) this.#increaseValue(primaryOrSecondary);
            else this.#decreaseValue(primaryOrSecondary);
            break;
          default:
            break;
        }
      }
    });

    this.onEvent('keyup', this, (event: { code: string; preventDefault: () => void; target: { name: string; }; key: any; }) => {
      if (this.readonly || this.disabled) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(event.code) > -1) {
        event.preventDefault();

        // Re-enables animation on non-step sliders when using keys
        if (this.type !== 'step') this.#toggleAnimations(true);
      }
    });
  }

  /**
   * Attaches events to the component related to focusin/focusout and hoverend/mouseleave behaviors
   * @private
   */
  #attachFocusListeners(): void {
    this.onEvent('mouseenter', this, () => {
      this.#mouseHover = true;
    });

    this.onEvent('mouseleave', this, () => {
      this.#mouseHover = false;
    });

    this.onEvent('hoverend.slider', this.thumbDraggable, () => {
      if (!this.disabled && !this.readonly) {
        this.#updateTooltipDisplay(false);
      }
    });

    this.onEvent('mouseleave.slider', this.thumbDraggable, () => {
      if (!this.disabled && !this.readonly && !this.shadowRoot?.activeElement) {
        this.#updateTooltipDisplay(true);
      }
    });

    if (this.type === 'range' && this.thumbDraggableSecondary) {
      this.onEvent('hoverend.slider', this.thumbDraggableSecondary, () => {
        if (!this.disabled && !this.readonly) {
          this.#updateTooltipDisplay(false, 'secondary');
        }
      });

      this.onEvent('mouseleave.slider', this.thumbDraggableSecondary, () => {
        if (!this.disabled && !this.readonly && !this.shadowRoot?.activeElement) {
          this.#updateTooltipDisplay(true, 'secondary');
        }
      });
    }

    // FOCUS/BLUR EVENTS
    this.onEvent('focusin', this.container, (e: FocusEvent) => {
      if (!this.disabled && this.shadowRoot?.activeElement) {
        this.#updateTooltipDisplay(false);
        const target = e.target instanceof HTMLElement && e.target;

        if (target && target?.classList.contains('secondary')) {
          this.#updateThumbShadow(false, 'secondary');
          this.#updateThumbShadow(true, 'primary');
        } else {
          this.#updateThumbShadow(false, 'primary');
          if (this.type === 'range') {
            this.#updateThumbShadow(true, 'secondary');
          }
        }

        if (this.type === 'range') {
          this.#updateTooltipDisplay(false, 'secondary');
        }
      }
    });

    this.onEvent('focusout', this.container, () => {
      if (!this.shadowRoot?.activeElement && !this.#mouseHover) {
        this.#updateTooltipDisplay(true);
        this.#updateThumbShadow(true, 'primary');
        if (this.type === 'range') {
          this.#updateTooltipDisplay(true, 'secondary');
          this.#updateThumbShadow(true, 'secondary');
        }
      }
    });
  }

  /**
   * Helper method for arrow key actions
   * @private
   * @param {string} primaryOrSecondary the primary or secondary value
   */
  #decreaseValue(primaryOrSecondary: string): void {
    switch (this.type) {
      case 'step':
        this.value -= ((this.max - this.min) / (this.stepNumber - 1));
        break;
      case 'range':
        if (primaryOrSecondary === 'secondary') {
          this.valueSecondary = Math.max(Math.ceil(this.valueSecondary) - 1, this.value);
        } else {
          this.value = Math.max(Math.ceil(this.value) - 1, this.min);
        }
        break;
      default:
        this.value = Math.ceil(this.value) - 1;
    }
  }

  /**
   * Helper method for arrow key actions
   * @private
   * @param {string} primaryOrSecondary the primary or secondary value
   */
  #increaseValue(primaryOrSecondary: string): void {
    switch (this.type) {
      case 'step':
        this.value = Math.min(this.max, Math.max(this.min, this.value + (this.max - this.min) / (this.stepNumber - 1)));
        break;
      case 'range':
        if (primaryOrSecondary === 'secondary') {
          this.valueSecondary = Math.min(Math.ceil(this.valueSecondary) + 1, this.max);
        } else {
          this.value = Math.min(Math.ceil(this.value) + 1, this.valueSecondary);
        }
        break;
      default:
        this.value = Math.ceil(this.value) + 1;
    }
  }
}
